import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Interactive elements inside `container` that can actually receive focus right now: enabled,
// not hidden, and laid out on screen.
export function getFocusable(container) {
  if (!container) return [];
  return [...container.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && el.getClientRects().length > 0
  );
}

/**
 * Keyboard behaviour for a modal drawer or dialog (WCAG 2.2 AA: 2.1.2, 2.4.3, 2.4.7):
 *   - on open, focus moves inside and lands on the first interactive element
 *   - Tab / Shift+Tab cycle within it, so focus can't wander behind it
 *   - Escape closes it (unless `canClose` is false, e.g. while saving)
 *   - on close, focus returns to whatever opened it
 *
 * `panelRef` is the dialog element; give it role="dialog", aria-modal="true", aria-labelledby
 * and tabIndex={-1} (the fallback focus target if it has nothing interactive).
 * `stepKey`: change it when the dialog swaps its content (multi-step flows) so focus moves to the
 * first interactive element of the new step.
 * `paused`: true while another dialog is open on top of this one (e.g. a confirmation). This one
 * then stops trapping and ignores Escape, and the top dialog's own trap takes over.
 */
export function useModalFocus({ isOpen, panelRef, onClose, stepKey, canClose = true, paused = false }) {
  const onCloseRef = useRef(onClose);
  const canCloseRef = useRef(canClose);
  const pausedRef = useRef(paused);
  onCloseRef.current = onClose;
  canCloseRef.current = canClose;
  pausedRef.current = paused;

  // Remember the opener, and hand focus back to it on close (if it's still on the page).
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  // Move focus in on open, and again whenever the step changes. Leave it alone if something inside
  // the dialog already took focus on purpose (e.g. an error summary that moves focus to itself).
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const active = document.activeElement;
      if (active && active !== panel && panel.contains(active)) return;
      (getFocusable(panel)[0] || panel).focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen, stepKey, panelRef]);

  // Escape to close; trap Tab inside.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (pausedRef.current) return;
      if (event.key === 'Escape') {
        if (canCloseRef.current) {
          event.stopPropagation();
          onCloseRef.current?.();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const nodes = getFocusable(panel);
      if (!nodes.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const inside = panel.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, panelRef]);
}
